"use client";

import { createContext, useContext, ReactNode, useEffect, useRef } from "react";
import { useSession, signIn, signOut } from "next-auth/react";
import { useRouter } from "next/navigation";

/**
 * AuthContext provides user, token, and auth actions.
 *
 * Session sync: When the user has a role (investor, realtor, property_manager)
 * but the session is missing the corresponding profile_id, AuthProvider runs a
 * one-time session refresh so the JWT callback fetches /profiles/status and
 * populates investor_id, realtor_id, property_manager_id. All pages using
 * useAuth() then see the correct user without per-page refresh logic.
 */

// User type that matches what the app expects
interface User {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  roles: string[];
  investor_id?: number;
  realtor_id?: number;
  property_manager_id?: number;
}

/** Extended session user fields beyond NextAuth defaults. */
interface SessionUser {
  id?: string;
  sub?: string;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  roles?: string[];
  investor_id?: number;
  realtor_id?: number;
  property_manager_id?: number;
  api_token?: string;
}

/** Extended session with custom fields from JWT callback. */
interface ExtendedSession {
  user?: SessionUser;
  accessToken?: string;
  sub?: string;
}

/** Sign-up request payload. */
export interface SignUpData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

/** Optional session update payload (e.g. after adding investor profile). */
export type SessionUpdatePayload = {
  investor_id?: number;
  realtor_id?: number;
  property_manager_id?: number;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<void>;
  signOut: () => void;
  /** Refresh session; pass profile ids to update JWT/session immediately (e.g. after adding investor). */
  refreshSession: (updateData?: SessionUpdatePayload) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status, update } = useSession();
  const router = useRouter();

  const loading = status === "loading";

  // Map NextAuth session to User object
  const extSession = session as unknown as ExtendedSession | null;
  const sessionUser = extSession?.user;
  const user: User | null = sessionUser
    ? {
        id: sessionUser.id || extSession?.sub || "",
        email: sessionUser.email || "",
        name: sessionUser.name || undefined,
        first_name: sessionUser.first_name,
        last_name: sessionUser.last_name,
        roles: [sessionUser.role || "user"],
        investor_id: sessionUser.investor_id,
        realtor_id: sessionUser.realtor_id,
        property_manager_id: sessionUser.property_manager_id,
      }
    : null;

  // Get access token from session (generated by NextAuth JWT callback)
  const token: string | null = extSession?.accessToken || null;

  // Traditional sign in using NextAuth credentials provider
  const handleSignIn = async (email: string, password: string) => {
    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });

    if (result?.error) {
      throw new Error(
        result.error === "CredentialsSignin"
          ? "Invalid email or password"
          : result.error,
      );
    }

    if (result?.ok) {
      router.push("/overview");
    }
  };

  // Sign up: account creation is intended to happen on the parent app (prop.cafe); UI should link there.
  // This handler is for programmatic/backend use only; PM app does not expose a signup form.
  const handleSignUp = async (signUpData: SignUpData) => {
    const { getClientApiUrl } = await import("@/lib/config/client");
    const API_URL = getClientApiUrl();

    // 1. Create user in backend
    const response = await fetch(`${API_URL}/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signUpData),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || "Sign-up failed");
    }

    // 2. Sign in with NextAuth to create session
    const result = await signIn("credentials", {
      email: signUpData.email,
      password: signUpData.password,
      redirect: false,
    });

    if (result?.error) {
      throw new Error(result.error);
    }

    if (result?.ok) {
      router.push("/overview");
    }
  };

  // Sign out using NextAuth
  const handleSignOut = () => {
    signOut({ callbackUrl: "/login" });
  };

  // Refresh session (triggers NextAuth session update). Pass profile ids to update JWT immediately.
  const refreshSession = async (updateData?: SessionUpdatePayload) => {
    await update(updateData);
  };

  // One-time session sync: when user has a role but missing profile_id, refresh session once
  // so JWT callback fetches /profiles/status and populates investor_id/realtor_id/property_manager_id.
  // All pages using useAuth() then get the correct user without per-page refresh logic.
  const hasTriedProfileSync = useRef(false);
  useEffect(() => {
    if (status !== "authenticated" || !session?.user) {
      if (status === "unauthenticated") hasTriedProfileSync.current = false;
      return;
    }
    const u = session.user as {
      role?: string;
      roles?: string[];
      investor_id?: number;
      realtor_id?: number;
      property_manager_id?: number;
    };
    const roles = u.roles ?? (u.role ? [u.role] : []);
    const needsInvestorId = roles.includes("investor") && u.investor_id == null;
    const needsRealtorId = roles.includes("realtor") && u.realtor_id == null;
    const needsPmId =
      roles.includes("property_manager") && u.property_manager_id == null;
    if ((needsInvestorId || needsRealtorId || needsPmId) && !hasTriedProfileSync.current) {
      hasTriedProfileSync.current = true;
      update().then(() => {
        // Session updated; all consumers of useAuth() will re-render with correct profile ids
      });
    }
  }, [status, session?.user, update]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        signIn: handleSignIn,
        signUp: handleSignUp,
        signOut: handleSignOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
